import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { readWithTimeout } from '../lib/supabaseRead';
import { ZONE_COLORS } from '../lib/constants';
import { trackAppEvent } from '../lib/analytics';
import { showToast } from '../lib/toast';
import { readZonesCache, writeZonesCache, removeLegacyAccountCaches } from '../lib/userScopedCache';

const SAVE_FAIL_TOAST = "Couldn't save — check connection and retry.";

export function useWatchZones(session, { maxZones = Infinity } = {}) {
  const [zones, setZones] = useState([]);
  const watchZonesUserId = session?.user?.id || null;

  const identityRef = useRef({ userId: watchZonesUserId, generation: 0, mounted: true });
  if (identityRef.current.userId !== watchZonesUserId) {
    identityRef.current = {
      userId: watchZonesUserId,
      generation: identityRef.current.generation + 1,
      mounted: true,
    };
  }
  useEffect(() => () => { identityRef.current.mounted = false; }, []);
  const captureIdentity = () => ({
    userId: identityRef.current.userId,
    generation: identityRef.current.generation,
  });
  const isCurrentIdentity = token => Boolean(
    identityRef.current.mounted
    && token.userId === identityRef.current.userId
    && token.generation === identityRef.current.generation
  );

  // Skip the persist write on the render immediately after an identity change so
  // the previous user's zones are never written under the new user's scoped key.
  const skipNextPersist = useRef(false);

  const nextZoneColor = (currentZones) => {
    const used = new Set(currentZones.map(z => z.color));
    return ZONE_COLORS.find(c => !used.has(c)) || ZONE_COLORS[currentZones.length % ZONE_COLORS.length];
  };

  const isDuplicateZone = (name, lat, lng) =>
    zones.some(z =>
      z.name.toLowerCase() === name.toLowerCase() ||
      (Math.abs(z.lat - lat) < 0.001 && Math.abs(z.lng - lng) < 0.001)
    );

  // Keyed to the user id (not the session object) so token refreshes don't refetch;
  // read is timeout-guarded so a wedged request falls back to THIS user's cached
  // zones (never an unscoped key) instead of leaving the map zone-less until an
  // app restart.
  useEffect(() => {
    removeLegacyAccountCaches();
    if (!watchZonesUserId) {
      // Signed-out state hydrates nothing.
      skipNextPersist.current = true;
      setZones([]);
      return;
    }

    // Immediate hydrate from THIS user's scoped cache; also purges the previous
    // user's in-memory zones before the first render of authenticated data.
    skipNextPersist.current = true;
    setZones(readZonesCache(watchZonesUserId));

    let cancelled = false;
    const load = async () => {
      const { data, error } = await readWithTimeout(
        signal => supabase
          .from('watch_zones')
          .select('id, name, latitude, longitude, radius_km')
          .eq('user_id', watchZonesUserId)
          .eq('is_active', true)
          .order('created_at')
          .abortSignal(signal),
        { label: 'watch_zones_load' }
      );
      if (cancelled) return;
      if (error) {
        // Keep the per-user cached zones hydrated above; never fall back to an
        // unscoped key that could belong to another account.
        console.warn('[useWatchZones] load failed:', error.message);
        return;
      }
      setZones(data && data.length > 0
        ? data.map((z, i) => ({ id: z.id, name: z.name, lat: z.latitude, lng: z.longitude, radius: z.radius_km * 1000, color: ZONE_COLORS[i % ZONE_COLORS.length] }))
        : []
      );
    };
    load();
    return () => { cancelled = true; };
  }, [watchZonesUserId]);

  // Persist to this user's scoped cache. Never write an unscoped key, and skip the
  // transitional render right after an identity change.
  useEffect(() => {
    if (skipNextPersist.current) { skipNextPersist.current = false; return; }
    if (!watchZonesUserId) return; // signed-out writes nothing
    writeZonesCache(watchZonesUserId, zones);
  }, [zones, watchZonesUserId]);

  const addZone = async ({ name, selLoc, radius }) => {
    if (isDuplicateZone(name, selLoc.lat, selLoc.lng)) return false;
    const limit = Number(maxZones);
    if (Number.isFinite(limit) && zones.length >= limit) {
      const zoneLabel = limit === 1 ? 'watch zone' : 'watch zones';
      showToast(`Free Standard can save up to ${limit} ${zoneLabel}. Upgrade to Pro for more.`);
      trackAppEvent('watch_zone_limit_reached', { limit, source: 'watch_zone_manager' }, { page: 'map', source: 'watch_zone_manager' });
      return 'limit';
    }
    const operationIdentity = captureIdentity();
    if (!operationIdentity.userId) return 'error';
    // Persist first and read the result — never leave an unsaved zone on screen.
    const { data, error } = await supabase.from('watch_zones').insert({ user_id: operationIdentity.userId, name, latitude: selLoc.lat, longitude: selLoc.lng, radius_km: radius / 1000, is_active: true }).select().single();
    if (!isCurrentIdentity(operationIdentity)) return false;
    if (error || !data) {
      console.warn('[useWatchZones] add failed:', error?.message);
      showToast(SAVE_FAIL_TOAST);
      return 'error';
    }
    setZones(p => [...p, { id: data.id, name, lat: selLoc.lat, lng: selLoc.lng, radius, color: nextZoneColor(p) }]);
    trackAppEvent('watch_zone_create', {
      name,
      radius_m: radius,
      latitude: selLoc.lat,
      longitude: selLoc.lng,
      source: 'watch_zone_manager',
    }, { page: 'map', source: 'watch_zone_manager' });
    return true;
  };

  const addZoneFromMap = async (preset, radius = 5000) => {
    if (isDuplicateZone(preset.name, preset.lat, preset.lng)) return false;
    const limit = Number(maxZones);
    if (Number.isFinite(limit) && zones.length >= limit) {
      const zoneLabel = limit === 1 ? 'watch zone' : 'watch zones';
      showToast(`Free Standard can save up to ${limit} ${zoneLabel}. Upgrade to Pro for more.`);
      trackAppEvent('watch_zone_limit_reached', { limit, source: 'map_long_press' }, { page: 'map', source: 'map_long_press' });
      return 'limit';
    }
    const operationIdentity = captureIdentity();
    if (!operationIdentity.userId) return 'error';
    const { data, error } = await supabase.from('watch_zones').insert({ user_id: operationIdentity.userId, name: preset.name, latitude: preset.lat, longitude: preset.lng, radius_km: radius / 1000, is_active: true }).select().single();
    if (!isCurrentIdentity(operationIdentity)) return false;
    if (error || !data) {
      console.warn('[useWatchZones] add-from-map failed:', error?.message);
      showToast(SAVE_FAIL_TOAST);
      return 'error';
    }
    setZones(p => [...p, { id: data.id, name: preset.name, lat: preset.lat, lng: preset.lng, radius, color: nextZoneColor(p) }]);
    trackAppEvent('watch_zone_create', {
      name: preset.name,
      radius_m: radius,
      latitude: preset.lat,
      longitude: preset.lng,
      council: preset.council || null,
      source: 'map_long_press',
    }, { page: 'map', source: 'map_long_press' });
    return true;
  };

  const updateZoneRadius = async (id, radius) => {
    const operationIdentity = captureIdentity();
    if (!operationIdentity.userId) return;
    const prev = zones.find(z => z.id === id);
    setZones(p => p.map(z => z.id === id ? { ...z, radius } : z));
    const { error } = await supabase.from('watch_zones').update({ radius_km: radius / 1000 }).eq('id', id).eq('user_id', operationIdentity.userId);
    if (!isCurrentIdentity(operationIdentity)) return;
    if (error) {
      console.warn('[useWatchZones] radius update failed:', error.message);
      // Roll back to the previous radius.
      if (prev) setZones(p => p.map(z => z.id === id ? { ...z, radius: prev.radius } : z));
      showToast(SAVE_FAIL_TOAST);
      return;
    }
    trackAppEvent('watch_zone_radius_update', { watch_zone_id: id, radius_m: radius }, { page: 'map', source: 'watch_zone_manager' });
  };

  const deleteZone = async (id) => {
    const operationIdentity = captureIdentity();
    if (!operationIdentity.userId) return;
    const removed = zones.find(z => z.id === id);
    setZones(p => p.filter(x => x.id !== id));
    const { error } = await supabase.from('watch_zones').delete().eq('id', id).eq('user_id', operationIdentity.userId);
    if (!isCurrentIdentity(operationIdentity)) return;
    if (error) {
      console.error('Failed to delete watch zone:', error);
      // Roll back: restore the zone we optimistically removed.
      if (removed) setZones(p => (p.some(z => z.id === id) ? p : [...p, removed]));
      showToast(SAVE_FAIL_TOAST);
      return;
    }
    trackAppEvent('watch_zone_delete', { watch_zone_id: id }, { page: 'map', source: 'watch_zone_manager' });
  };

  return { zones, addZone, addZoneFromMap, updateZoneRadius, deleteZone };
}
